class PredictiveSearch extends HTMLFormElement {
  constructor() {
    super();
    this.cachedMap = new Map();
    this.focusElement = this.input;
    this.resetButton.addEventListener('click', this.clear.bind(this));
    this.input.addEventListener('input', FoxTheme.utils.debounce(this.onChange.bind(this), 300));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.input.addEventListener('keydown', this.onKeydown.bind(this));
    this.searchContent = this.querySelector('.search__content');
    this.statusElement = this.querySelector('[data-search-status]');
    this.searchRecommendationEmpty = this.dataset.searchRecommendationEmpty === 'true';
    this.activeOptionIndex = -1;

    const isTemplateSearch = this.closest('.template-search');
    if (isTemplateSearch) {
      document.addEventListener('click', this.handleClickOutside.bind(this));
    }
  }

  get input() {
    return this.querySelector('input[type="search"]');
  }
  get resetButton() {
    return this.querySelector('button[type="reset"]');
  }

  onFocus(event) {
    if (this.closest('.template-search')) {
      document.body.classList.add('predictive-search-open');
      if (this.getQuery().length === 0) {
        return;
      }
      const url = this.setupURL().toString();
      this.renderSection(url, event);
    }
  }
  getQuery() {
    return this.input.value.trim();
  }
  clear(event = null) {
    event && event.preventDefault();
    if (this.searchRecommendationEmpty && this.searchContent) {
      this.searchContent.classList.add('hidden');
    }
    this.input.value = '';
    this.input.focus();
    this.removeAttribute('results');
    this.removeAttribute('error');
    this.setActiveOption(-1);
    this.input.setAttribute('aria-expanded', 'false');
  }

  setupURL() {
    // Resolve against the current origin, not window.shopUrl: on `shopify theme
    // dev` (127.0.0.1:9292) shopUrl is the myshopify domain and the fetch dies
    // on CORS. Same-origin everywhere in production.
    const url = new URL(FoxTheme.routes.predictive_search_url, window.location.origin);
    return (
      url.searchParams.set('q', this.getQuery()),
      // Figma 3822:48480 / 3822:48470 draw exactly three result types - products,
      // query suggestions and pages. Collections and articles have no design peer
      // (scope brief section 12, delta J) and are not requested.
      url.searchParams.set('resources[type]', 'product,query,page'),
      url.searchParams.set('resources[limit]', this.dataset.resultsLimit || 4),
      url.searchParams.set('resources[limit_scope]', 'each'),
      // Predictive search omits SKU/barcode from its default field set, so a
      // typed SKU (e.g. TABMONTG240) found nothing here but matched on the full
      // /search page. Name every searchable field so SKU lookups work too.
      url.searchParams.set(
        'resources[options][fields]',
        'title,product_type,variants.title,vendor,variants.sku,variants.barcode,tag,author,body'
      ),
      url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this)),
      url
    );
  }

  onChange() {
    if (this.getQuery().length === 0) {
      this.clear();
      return;
    }
    const url = this.setupURL().toString();
    this.renderSection(url);
  }

  renderSection(url) {
    this.cachedMap.has(url) ? this.renderSectionFromCache(url) : this.renderSectionFromFetch(url);
  }

  renderSectionFromCache(url) {
    const responseText = this.cachedMap.get(url);
    this.renderSearchResults(responseText), this.setAttribute('results', '');
  }

  renderSectionFromFetch(url) {
    // Abort any in-flight request so a slow "ch" response can never overwrite
    // a fast "chair" one (search-drawer brief section 6). Named fetchController:
    // ModalComponent already owns this.abortController for listener teardown.
    this.fetchController?.abort();
    this.fetchController = new AbortController();
    const { signal } = this.fetchController;

    // Indicate that loading has started
    this.setAttribute('loading', 'true');
    this.resetButton.classList.add('btn--loading');

    fetch(url, { signal })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.text();
      })
      .then((responseText) => {
        // Process and cache the fetched data
        this.renderSearchResults(responseText);
        this.cachedMap.set(url, responseText);
        this.removeAttribute('error');
        // Only the success path flags results - the previous implementation set it
        // in finally, so the error state was also styled as having results.
        this.setAttribute('results', 'true');
      })
      .catch((error) => {
        // A deliberate abort (newer query or drawer close) is not an error state.
        if (error.name === 'AbortError') return;
        console.error('Error fetching data: ', error);
        this.setAttribute('error', 'Failed to load data');
      })
      .finally(() => {
        // Remove the loading attribute in both success and error cases
        this.removeAttribute('loading');
        this.resetButton.classList.remove('btn--loading');
      });
  }
  renderSearchResults(responseText) {
    const id = 'PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this);
    const targetElement = document.getElementById(id);

    if (targetElement) {
      const parser = new DOMParser();
      const parsedDoc = parser.parseFromString(responseText, 'text/html');
      const contentElement = parsedDoc.getElementById(id);

      if (contentElement) {
        this.searchContent?.classList.remove('hidden');
        targetElement.innerHTML = contentElement.innerHTML;
        // Combobox pattern owns traversal (scope brief assertion 91): options and
        // their inner links/buttons are not tabbable - focus stays in the input and
        // aria-activedescendant reflects the active option.
        targetElement.querySelectorAll('[role="option"] a, [role="option"] button').forEach((el) => {
          el.setAttribute('tabindex', '-1');
        });
        this.setActiveOption(-1);
        this.input.setAttribute('aria-expanded', 'true');
        this.announceResults(targetElement);
      } else {
        console.error(`Element with id '${id}' not found in the parsed response.`);
      }
    } else {
      console.error(`Element with id '${id}' not found in the document.`);
    }
  }

  /* Announce the render outcome via the aria-live region OUTSIDE the swapped
     container (predictive-search.liquid [data-search-status]), so the announcement
     survives the innerHTML replacement. */
  announceResults(targetElement) {
    if (!this.statusElement) return;
    const strings = window.__predictiveSearchStrings || {};
    const count = targetElement.querySelectorAll('[role="option"]').length;
    let message;
    if (count > 0) {
      message = (strings.results || '[count] results for [terms]')
        .replace('[count]', count)
        .replace('[terms]', this.getQuery());
    } else {
      message = (strings.noResults || 'No results found for [terms]').replace('[terms]', this.getQuery());
    }
    this.statusElement.textContent = message;
  }

  /* Combobox keyboard traversal: linear across every [role="option"] in DOM order
     (queries -> pages -> products). Focus stays in the input; the active option is
     reflected via aria-activedescendant + aria-selected (scope brief assertions
     85-91). Options are not tabbable - the combobox pattern owns traversal. */
  get options() {
    const id = 'PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this);
    const container = document.getElementById(id);
    return container ? Array.from(container.querySelectorAll('[role="option"]')) : [];
  }

  setActiveOption(index) {
    const options = this.options;
    options.forEach((option, i) => {
      option.setAttribute('aria-selected', i === index ? 'true' : 'false');
      option.classList.toggle('is-active', i === index);
    });
    this.activeOptionIndex = index;
    if (index >= 0 && options[index]) {
      this.input.setAttribute('aria-activedescendant', options[index].id);
      options[index].scrollIntoView({ block: 'nearest' });
    } else {
      this.input.removeAttribute('aria-activedescendant');
    }
  }

  onKeydown(event) {
    const options = this.options;
    if (options.length === 0) return;

    switch (event.key) {
      case 'ArrowDown': {
        event.preventDefault();
        const next = this.activeOptionIndex >= options.length - 1 ? 0 : this.activeOptionIndex + 1;
        this.setActiveOption(next);
        break;
      }
      case 'ArrowUp': {
        event.preventDefault();
        const prev = this.activeOptionIndex <= 0 ? options.length - 1 : this.activeOptionIndex - 1;
        this.setActiveOption(prev);
        break;
      }
      case 'Home':
        event.preventDefault();
        this.setActiveOption(0);
        break;
      case 'End':
        event.preventDefault();
        this.setActiveOption(options.length - 1);
        break;
      case 'Enter': {
        if (this.activeOptionIndex >= 0 && options[this.activeOptionIndex]) {
          const link = options[this.activeOptionIndex].querySelector('a[href]');
          if (link) {
            event.preventDefault();
            window.location.assign(link.href);
          }
        }
        // No active option: fall through to the native form submit.
        break;
      }
      case 'Escape':
        // Shell behaviour (search-drawer close) - never swallowed here.
        break;
    }
  }

  handleClickOutside(event) {
    if (!this.contains(event.target)) {
      setTimeout(() => {
        document.body.classList.remove('predictive-search-open');
      });
    }
  }
}
customElements.define('predictive-search', PredictiveSearch, { extends: 'form' });

class SearchDrawer extends DrawerComponent {
  constructor() {
    super();
    // QA P1 fix: FoxTheme.a11y.trapFocus derives first/last from ALL
    // focusables, including hidden ones (e.g. the mobile footer CTA at
    // desktop), so its wrap never fires from the last VISIBLE element and
    // focus escapes the drawer. Intercept Tab here (capture runs before the
    // vendor's document-level handler) and wrap across visible focusables.
    this.addEventListener('keydown', (event) => {
      if (event.code?.toUpperCase() !== 'TAB' || !this.open) return;
      const visible = FoxTheme.a11y.getFocusableElements(this).filter(
        (el) => el.offsetParent !== null || el.getClientRects().length > 0
      );
      if (visible.length === 0) return;
      const first = visible[0];
      const last = visible[visible.length - 1];
      if (!event.shiftKey && event.target === last) {
        event.preventDefault();
        event.stopPropagation();
        first.focus();
      } else if (event.shiftKey && (event.target === first || event.target === this)) {
        event.preventDefault();
        event.stopPropagation();
        last.focus();
      }
    });
  }
  get requiresBodyAppended() {
    return false;
  }
  hide() {
    if (this.open) {
      // Abort any in-flight predictive fetch so a resolving response cannot
      // repaint the results while (or after) the drawer closes.
      this.querySelector('form[is="predictive-search"]')?.fetchController?.abort();
    }
    return super.hide();
  }
  get input() {
    return this.querySelector('input[type="search"]');
  }
  get focusElement() {
    return this.querySelector('input[type="search"]');
  }
}
customElements.define('search-drawer', SearchDrawer);
